/**
 * TaskCreateContext — ouverture GLOBALE de la modale « Nouvelle tâche » avec un
 * lien d'entité EXPLICITE (WKF-013).
 *
 * Motif : la cloche et les fiches pré-remplissent le lien depuis
 * `PageEntityContext` (fiche courante). Mais depuis le menu ⋮ d'une LIGNE de
 * tableau, l'entité visée n'est PAS la page courante (on est sur une liste) — il
 * faut donc passer le lien de la ligne explicitement. Ce contexte expose
 * `openNewTask({type, id, label})` ; la modale est montée une seule fois dans
 * `AppLayout` (`GlobalTaskCreateDialog`) et pré-remplie avec ce lien.
 */

import { useCallback, useMemo, useRef, useState } from 'react'

import { TaskCreateDialog } from '@/components/notifications/TaskCreateDialog'
import { useEntity } from '@/contexts/EntityContext'

import {
    TaskCreateContext,
    type TaskCreateContextValue,
    type TaskCreateOptions,
} from './TaskCreateContext.helpers'

/** Lien d'entité à pré-remplir dans la tâche (dérivé du registre de recherche). */
export interface PendingTaskLink {
    type: string
    id: string
    label: string
}

/** Ce que l'appelant a demandé de PRÉ-REMPLIR — ni l'un ni l'autre obligatoire. */
type Prefill = Pick<TaskCreateOptions, 'initialTitle' | 'initialDueDate'>

export function TaskCreateProvider({ children }: { children: React.ReactNode }) {
    const [pending, setPending] = useState<PendingTaskLink | null>(null)
    /* Le pré-remplissage est de la DONNÉE DE RENDU, pas un rappel : il vit donc
       dans l'état, à côté du lien, et se pose dans la même passe que lui (React
       groupe les deux `setState` d'un même gestionnaire). Le mettre dans une ref
       marcherait par accident — la modale n'est montée qu'au moment où `pending`
       devient non nul — mais un changement de pré-remplissage à modale ouverte
       ne repeindrait rien, et la panne serait muette. */
    const [prefill, setPrefill] = useState<Prefill>({})
    /* Le rappel, LUI, vit dans une ref : le mettre dans l'état recomposerait
       `value` à chaque ouverture, et `openNewTask` cesserait d'être stable — les
       `useMemo` de row-actions qui l'ont en dépendance se recalculeraient à
       chaque clic. */
    const rappel = useRef<TaskCreateOptions['onCreated']>(undefined)

    const openNewTask = useCallback((link: PendingTaskLink, options?: TaskCreateOptions) => {
        rappel.current = options?.onCreated
        setPrefill({
            initialTitle: options?.initialTitle,
            initialDueDate: options?.initialDueDate,
        })
        setPending(link)
    }, [])
    const close = useCallback(() => {
        rappel.current = undefined
        setPrefill({})
        setPending(null)
    }, [])

    // `openNewTask` est stable → les hooks de row-actions qui le mettent en deps
    // ne recomposent pas leur `useMemo` quand la modale s'ouvre/se ferme.
    const value = useMemo<TaskCreateContextValue>(() => ({ openNewTask }), [openNewTask])

    return (
        <TaskCreateContext.Provider value={value}>
            {children}
            <GlobalTaskCreateDialog
                pending={pending}
                prefill={prefill}
                onClose={close}
                onCreated={(taskId, lien) => rappel.current?.(taskId, lien)}
            />
        </TaskCreateContext.Provider>
    )
}

/**
 * Modale rendue UNIQUEMENT quand une tâche est demandée (`pending` non nul) : on
 * évite ainsi que ses hooks (mutations, requête destinataires) tournent sur
 * CHAQUE page alors qu'elle est fermée. Montée dans le provider (global).
 */
function GlobalTaskCreateDialog({
    pending,
    prefill,
    onClose,
    onCreated,
}: {
    pending: PendingTaskLink | null
    prefill: Prefill
    onClose: () => void
    onCreated: NonNullable<TaskCreateOptions['onCreated']>
}) {
    const { currentEntity } = useEntity()
    if (!pending) return null
    /* `?? null` sur les deux pré-remplissages : la modale doit distinguer « non
       demandé » — champ vide, le comportement de TOUS les appelants historiques,
       qui n'en passent aucun — d'une valeur explicitement fournie. */
    return (
        <TaskCreateDialog
            open
            onOpenChange={(o) => {
                if (!o) onClose()
            }}
            entityId={currentEntity?.entityId ?? null}
            linkedEntityType={pending.type}
            linkedEntityId={pending.id}
            linkedLabel={pending.label}
            initialTitle={prefill.initialTitle ?? null}
            initialDueDate={prefill.initialDueDate ?? null}
            onCreated={onCreated}
        />
    )
}
